// Active deals grouped by loan stage. The stage list and its order are the pipeline's own: /api/me `stages`,
// served from the `loan_stage` table (apps/pipeline_api/migrations/001_pipeline_v1.sql) and mirrored by
// pipeline_domain::stages::ALL. Nothing here decides which stage a loan is in; it only reads `stage_key`.
import type { Stage } from "./types";
import { stageDisplayAbbr } from "./loanSteps";

/** Section key for loans with no stage, or a stage key the stage list does not have. */
export const STAGE_NOT_SET = "stage_not_set";
export const STAGE_NOT_SET_LABEL = "Stage not set";

export interface StageSection<T> {
  key: string;
  /** null for the "Stage not set" section. */
  stage: Stage | null;
  label: string;
  items: T[];
}

/**
 * One section per stage in `sort` order, every stage present even when it has no loans, so the whole
 * funnel shows at a glance. Loans whose stage is missing or unknown go to a final "Stage not set" section,
 * which appears only when it has loans; no loan is dropped. Input order is kept inside each section.
 */
export function groupByStage<T>(items: readonly T[], stages: readonly Stage[], stageOf: (item: T) => string | null | undefined): StageSection<T>[] {
  const ordered = [...stages].sort((a, b) => a.sort - b.sort);
  const sections: StageSection<T>[] = ordered.map((stage) => ({ key: stage.key, stage, label: stage.label, items: [] }));
  const byKey = new Map(sections.map((s) => [s.key, s]));
  const notSet: StageSection<T> = { key: STAGE_NOT_SET, stage: null, label: STAGE_NOT_SET_LABEL, items: [] };
  for (const item of items) {
    const key = stageOf(item);
    const section = (key ? byKey.get(key) : undefined) ?? notSet;
    section.items.push(item);
  }
  if (notSet.items.length) sections.push(notSet);
  return sections;
}

export interface StageView<T> {
  sections: StageSection<T>[];
  /** Some loan's stage changed since the sections were frozen: offer "regroup". */
  stale: boolean;
}

/**
 * Stage sections for everything in view, including loans created this session (pass them first to keep them
 * at the top of their section). `frozen` holds each loan's stage when the view was last sorted, so rows never
 * jump between sections under the cursor; a loan missing from it is placed by its current stage.
 */
export function stageView<T extends { id: string; stage_key: string | null }>(loans: readonly T[], stages: readonly Stage[], frozen: ReadonlyMap<string, string | null>): StageView<T> {
  const stageOf = (l: T) => (frozen.has(l.id) ? frozen.get(l.id) : l.stage_key);
  return {
    sections: groupByStage(loans, stages, stageOf),
    stale: loans.some((l) => frozen.has(l.id) && frozen.get(l.id) !== l.stage_key),
  };
}

export type StageRow<T> = { type: "group"; key: string; label: string; summary: string; collapsed: boolean; tone?: { solid: string; on: string } } | { type: "loan"; loan: T };

/**
 * Grid rows for the stage sections: one header per section (empty ones say "None"), then its loans unless
 * collapsed. A stage the lender renames (lib/loanSteps.ts) carries its short name in the one-column label cell
 * and its full name at the start of the row-wide summary.
 */
export function stageRows<T>(
  sections: readonly StageSection<T>[],
  collapsed: ReadonlySet<string>,
  summarize: (items: T[]) => string,
  toneOf: (stage: Stage) => { solid: string; on: string } | null,
): StageRow<T>[] {
  const out: StageRow<T>[] = [];
  for (const sec of sections) {
    const key = `stage:${sec.key}`;
    const isCollapsed = collapsed.has(key);
    const tone = sec.stage ? toneOf(sec.stage) : null;
    const short = sec.stage ? stageDisplayAbbr(sec.stage) : undefined;
    const counts = sec.items.length ? summarize(sec.items) : "None";
    out.push({
      type: "group",
      key,
      label: sec.stage ? `${sec.stage.sort}. ${(short ?? sec.label).toUpperCase()}` : sec.label.toUpperCase(),
      summary: short ? `${sec.label}  ·  ${counts}` : counts,
      collapsed: isCollapsed,
      tone: tone ? { solid: tone.solid, on: tone.on } : undefined,
    });
    if (!isCollapsed) for (const loan of sec.items) out.push({ type: "loan", loan });
  }
  return out;
}
